import React, { useEffect, useState } from "react";
import "./AdDetail.scss";
import {
  Breadcrumb,
  Button,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Tag,
  Tooltip,
} from "antd";
import { useUnit } from "contexts/UnitContext";
import Helpers from "@utils/Helpers";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { AdApi } from "services/AdApi";
import { errorLog } from "logging";
import {
  InfoCircleOutlined,
  RetweetOutlined,
  StopOutlined,
} from "@ant-design/icons";

interface AdDetailProps {
  adData: any;
  adList: any;
  setAdList: (value: any) => void;
  adType: any;
  isUpdated: boolean;
  setIsUpdated: (value: boolean) => void;
}

const AdDetail: React.FC<AdDetailProps> = ({
  adData,
  adList,
  setAdList,
  adType,
  isUpdated,
  setIsUpdated,
}) => {
  const { units, convertCurrency } = useUnit();
  const [updatedAdData, setUpdatedAdData] = useState<any>(null);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isReposting, setIsReposting] = useState(false);
  const [unit, setUnit] = useState<string>("tons");
  const [discountedPrice, setDiscountedPrice] = useState<number>(0);
  const [originalPrice, setOriginalPrice] = useState<number>(0);
  const [isStopping, setIsStopping] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdData = async () => {
      try {
        const response = await AdApi.getAdDetail(adData?.product_ad_id);
        if (response.status === 200) {
          setUpdatedAdData({
            ...response.data.data,
            duration: response.data.data.duration / 7,
          });
          setSelectedAd(response.data.data);
          setOriginalPrice(
            convertCurrency(
              response.data.data?.product.price_per_tons,
              "USD",
              units.currency
            ) * 1000
          );
          setDiscountedPrice(
            Helpers.toFixedNoRound(
              convertCurrency(
                response.data.data?.discounted_price * 1000,
                "USD",
                units.currency
              ),
              2
            )
          );
          setIsStopping(
            response.data.data.ad_status.product_ad_status_id === 4
          );
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("AdDetail.tsx - fetchAdData", error);
      }
    };
    if (adData) {
      fetchAdData();
    }
  }, [adData]);

  useEffect(() => {
    if (!updatedAdData) return;
    const discounted =
      (1 - updatedAdData.discounted_percent / 100) *
      convertCurrency(
        updatedAdData?.product.price_per_tons,
        "USD",
        units.currency
      ) *
      1000;
    setDiscountedPrice(Helpers.toFixedNoRound(discounted, 2));
  }, []);

  const handleDiscountPercentageChange = (value: number) => {
    let discounted = 0;
    if (unit === "tons") {
      discounted = originalPrice - (originalPrice * value) / 100;
    } else {
      const pricePerBale =
        convertCurrency(
          updatedAdData?.product.current_price,
          "USD",
          units.currency
        ) / updatedAdData?.product.quantity;
      discounted = pricePerBale - (pricePerBale / 100) * value;
    }
    setDiscountedPrice(Helpers.toFixedNoRound(discounted, 2));
    setUpdatedAdData({
      ...updatedAdData,
      discounted_percent: value,
    });
  };

  const handleDiscountedPriceChange = (value: number) => {
    let percent = 0;
    if (unit === "tons") {
      percent = ((originalPrice - value) / originalPrice) * 100;
    } else {
      const pricePerBale =
        convertCurrency(
          updatedAdData?.product.current_price,
          "USD",
          units.currency
        ) / updatedAdData?.product.quantity;
      percent = ((pricePerBale - value) / originalPrice) * 100;
    }

    setDiscountedPrice(Helpers.toFixedNoRound(value, 2));
    setUpdatedAdData({
      ...updatedAdData,
      discounted_percent: Helpers.toFixedNoRound(percent, 0),
    });
  };

  const handleChangeUnit = (value: string) => {
    setUnit(value);
    if (value === "tons") {
      const discounted =
        originalPrice -
        (originalPrice * updatedAdData?.discounted_percent) / 100;
      setDiscountedPrice(Helpers.toFixedNoRound(discounted, 2));
    } else {
      const pricePerBale =
        convertCurrency(
          updatedAdData?.product.current_price,
          "USD",
          units.currency
        ) / updatedAdData?.product.quantity;

      const discountedPrice =
        pricePerBale - (pricePerBale / 100) * updatedAdData?.discounted_percent;
      setDiscountedPrice(Helpers.toFixedNoRound(discountedPrice, 2));
    }
  };

  const handleValidateAdForm = () => {
    let isValid = true;
    if (
      updatedAdData.discounted_percent === 0 ||
      updatedAdData.discounted_price === 0 ||
      updatedAdData.discounted_quantity === 0 ||
      !updatedAdData.discounted_quantity
    ) {
      Helpers.notification.warning("Please fill in all the fields.");
      isValid = false;
    }
    if (updatedAdData.discounted_percent % 1 !== 0) {
      Helpers.notification.warning("Discount percentage must be an integer");
      isValid = false;
    }
    if (updatedAdData.discounted_quantity > updatedAdData.product.quantity) {
      Helpers.notification.warning(
        `Discounted quantity must be less than or equal to stock (In stock: ${updatedAdData.product.quantity})`
      );
      isValid = false;
    }
    if (
      updatedAdData.discounted_percent > 100 ||
      updatedAdData.discounted_percent < 5
    ) {
      Helpers.notification.warning(
        "Discount percentage must be between 5 and 100"
      );
      isValid = false;
    }
    return isValid;
  };

  const handleUpdateAd = async () => {
    if (!handleValidateAdForm()) return;
    const body = {
      product_ad_id: updatedAdData?.product_ad_id,
      product_ad_status_id: updatedAdData?.ad_status.product_ad_status_id,
      discounted_price: convertCurrency(
        discountedPrice / 1000,
        units.currency,
        "USD"
      ),
      discounted_percent: updatedAdData?.discounted_percent,
      discounted_quantity: updatedAdData?.discounted_quantity,
      duration: updatedAdData?.duration * 7,
      start_time: updatedAdData?.start_time,
    };

    try {
      const response = await AdApi.updateAd(body);
      if (response.status === 200) {
        setIsEditing(false);
        Helpers.showSuccessUpdated("Update ad successfully!");
        const myProductCloned = structuredClone(adList);
        const responseData = response.data.data;
        myProductCloned.forEach((item: any) => {
          if (item.product_ad_id === responseData.product_ad_id) {
            item.product_ad_id = responseData.product_ad_id;
            item.product_ad_type_id = responseData.product_ad_type_id;
            item.discounted_percent = responseData.discounted_percent;
            item.discounted_price = responseData.discounted_price;
            item.discounted_quantity = responseData.discounted_quantity;
            item.duration = responseData.duration;
            item.start_time = responseData.start_time;
            item.ad_status.product_ad_status_id =
              responseData.product_ad_status_id;
          }
        });
        setAdList(myProductCloned);
        setIsUpdated(!isUpdated);
      }
    } catch (error) {
      Helpers.notification.error("Can not update ad. Please try again");
      errorLog("AdDetail.tsx - handleUpdateAd", error);
    }
  };

  const handleRepostAd = async () => {
    if (!handleValidateAdForm()) return;
    const body = {
      product_ad_id: updatedAdData?.product_ad_id,
      product_ad_type_id: updatedAdData?.product_ad_type_id,
      start_time: updatedAdData?.start_time,
      duration: updatedAdData?.duration * 7,
      discounted_price: convertCurrency(
        updatedAdData?.discounted_price / 1000,
        units.currency,
        "USD"
      ),
      discounted_percent: updatedAdData?.discounted_percent,
      discounted_quantity: updatedAdData?.discounted_quantity,
    };
    try {
      const response = await AdApi.repostAd(body);
      if (response.status === 201) {
        setIsReposting(false);
        setIsEditing(false);
        Helpers.showSuccessUpdated("Repost ad successfully!");
        setAdList(null);
        setIsUpdated(!isUpdated);
        navigate("/profile?direct=ads");
      }
    } catch (error) {
      Helpers.notification.error("Can not repost ad. Please try again");
      errorLog("AdDetail.tsx - handleRepostAd", error);
    }
  };

  const handleChangeStatus = async (action: string) => {
    const body = {
      product_ad_id: updatedAdData?.product_ad_id,
      action: action,
      description: updatedAdData?.last_action.description,
      start_time: updatedAdData?.last_action.start_time,
    };
    try {
      const response = await AdApi.changeStatus(body);
      if (response.status === 200) {
        setIsEditing(false);
        const myProductCloned = structuredClone(adList);
        const responseData = response.data.data;

        myProductCloned.forEach((item: any) => {
          if (item.product_ad_id === responseData.product_ad_id) {
            item.product_ad_id = responseData.product_ad_id;
            item.last_action.description = responseData.last_action.description;
            item.last_action.action = responseData.last_action.action_type;
            item.last_action.start_time = responseData.last_action.start_time;
            item.ad_status.product_ad_status_id =
              responseData.product_ad_status_id;
          }
        });
        setAdList(myProductCloned);
        setIsUpdated(!isUpdated);
        Helpers.showSuccessUpdated("Update ad status successfully!");
      }
    } catch (error) {
      Helpers.notification.error("Can not update ad status. Please try again");
      errorLog("AdDetail.tsx - handleChangeStatus", error);
    }
  };

  const handleCancel = (data: any) => {
    setUpdatedAdData({
      ...data,
      duration: data.duration / 7,
    });
    setDiscountedPrice(
      Helpers.toFixedNoRound(
        convertCurrency(data.discounted_price * 1000, "USD", units.currency),
        2
      )
    );
    setIsEditing(false);
    setIsReposting(false);
    if (data.ad_status.product_ad_status_id === 4) {
      setIsStopping(true);
    } else {
      setIsStopping(false);
    }
  };

  return (
    <div className="AdDetail">
      <Breadcrumb
        className="title"
        separator=">"
        items={[
          {
            title: "All Ads",
            href: "/profile?direct=ads",
          },
          {
            title: "Ad Details",
          },
        ]}
      />

      <div className="product-group">
        <div className="product-info">
          <div className="product-info-image">
            <img
              src={updatedAdData?.product.images[0]}
              alt={updatedAdData?.product.product_type_name}
            />
          </div>
          <div className="product-info-details">
            <div className="product-info-details-header">
              <span className="product-name">
                {updatedAdData?.product.product_type_name}
              </span>
              <span className="product-id">
                ID: {updatedAdData?.product.product_id}
              </span>
              <span className="product-price">
                ${Helpers.formatNumber(originalPrice, 2)} /{units.weight}
              </span>
            </div>
            <div className="ad-control">
              <Link to={""}>
                <span className="ad-control-item">Ad History</span>
              </Link>
              <Link to={""}>
                <span className="ad-control-item">Ad Performance</span>
              </Link>
            </div>
          </div>
        </div>
        <div className="product-status">
          <Tag
            color={
              updatedAdData?.ad_status.product_ad_status_id === 1
                ? "success"
                : updatedAdData?.ad_status.product_ad_status_id === 2
                ? "default"
                : updatedAdData?.ad_status.product_ad_status_id === 4
                ? "error"
                : "info"
            }
            className="product-status-tag"
          >
            {updatedAdData?.ad_status.title}
          </Tag>
          <div className="product-status-control">
            <Button
              className="product-status-control-item"
              onClick={() => {
                setIsReposting(true);
                setIsEditing(true);
                setIsStopping(false);
                const element = document.querySelector(".ad-info");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
                setUpdatedAdData({
                  ...updatedAdData,
                  start_time: dayjs().toISOString(),
                  end_time: dayjs().add(updatedAdData?.duration, "week"),
                });
              }}
            >
              <RetweetOutlined />
              Repost
            </Button>
            {
              <Button
                className="product-status-control-item stop-btn"
                disabled={updatedAdData?.ad_status.product_ad_status_id === 4}
                onClick={() => {
                  setIsStopping(true);
                  setIsEditing(true);
                  setIsReposting(false);
                  const element = document.querySelector(".ad-info");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                  setUpdatedAdData({
                    ...updatedAdData,
                    last_action: {
                      ...updatedAdData?.last_action,
                      start_time: dayjs().toISOString(),
                    },
                  });
                }}
              >
                <StopOutlined />
                Stop
              </Button>
            }
          </div>
        </div>
      </div>

      <div className={`ad-info ${isReposting || isStopping ? "edit" : ""}`}>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">
              Ads type{" "}
              <Tooltip
                placement="right"
                title={`Run for limited time promotions. Discount products or services with urgent call-to-action. Countdown timer displayed on the ad. Dedicated "Promoted Product" section on the homepage`}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <Select
              disabled={!isReposting || isStopping}
              variant="outlined"
              size="large"
              className="ad-info-select"
              value={updatedAdData?.product_ad_type_id}
              options={adType?.map((item: any) => ({
                label: item.name,
                value: item.product_ad_type_id,
              }))}
              onChange={(value: any) => {
                setIsEditing(true);
                setUpdatedAdData({
                  ...updatedAdData,
                  product_ad_type_id: value,
                });
              }}
            />
          </div>
          {isStopping && (
            <div className="ad-info-content">
              <span className="ad-info-label">Reason for stopping </span>
              <Select
                variant="outlined"
                size="large"
                className="ad-info-select"
                value={updatedAdData?.last_action.description}
                options={[
                  {
                    label: "Campaign ended early",
                    value: "Campaign ended early",
                  },
                  {
                    label: "Business strategy change",
                    value: "Business strategy change",
                  },
                  {
                    label: "No longer accepting orders",
                    value: "No longer accepting orders",
                  },
                  { label: "Others", value: "Others" },
                ]}
                onChange={(value: any) => {
                  setIsEditing(true);
                  setUpdatedAdData({
                    ...updatedAdData,
                    last_action: {
                      ...updatedAdData?.last_action,
                      description: value,
                    },
                  });
                }}
              />
            </div>
          )}
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">Duration</span>
            <Select
              className="ad-info-select"
              disabled={isStopping}
              variant="outlined"
              size="large"
              value={updatedAdData?.duration || 1}
              options={[
                { label: "1 week", value: 1 },
                { label: "2 weeks", value: 2 },
                { label: "3 weeks", value: 3 },
                { label: "4 weeks", value: 4 },
              ]}
              onChange={(value: any) => {
                setIsEditing(true);
                setUpdatedAdData({
                  ...updatedAdData,
                  duration: value,
                  end_time: dayjs(updatedAdData?.start_time)
                    .add(value, "week")
                    .toISOString(),
                });
              }}
            />
          </div>
          <div className="ad-info-content">
            <span className="ad-info-label">Slot for ads</span>
            <Input
              className="ad-info-input"
              disabled
              variant="outlined"
              size="large"
              value={updatedAdData?.duration || 1}
              suffix="slot"
            />
          </div>
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">Start date</span>
            <DatePicker
              className="ad-info-input"
              disabled={!isReposting || isStopping}
              variant="outlined"
              size="large"
              style={{ width: "100%" }}
              allowClear={false}
              minDate={dayjs()}
              value={
                updatedAdData?.start_time
                  ? dayjs(updatedAdData?.start_time)
                  : null
              }
              onChange={(value: any) => {
                setIsEditing(true);
                setUpdatedAdData({
                  ...updatedAdData,
                  start_time: value.toISOString(),
                  end_time: dayjs(value).add(updatedAdData?.duration, "week"),
                });
              }}
            />
          </div>

          <div className="ad-info-content">
            <span className="ad-info-label">
              {isStopping ? "Stop date" : "End date"}
            </span>
            <DatePicker
              className="ad-info-input"
              disabled
              variant="outlined"
              size="large"
              style={{ width: "100%" }}
              value={
                updatedAdData?.end_time ? dayjs(updatedAdData?.end_time) : null
              }
            />
          </div>
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">Discounted rate</span>
            <InputNumber
              className="ad-info-input"
              disabled={isStopping}
              variant="outlined"
              size="large"
              suffix="%"
              controls={false}
              value={Helpers.toFixedNoRound(
                updatedAdData?.discounted_percent,
                0
              )}
              min={5}
              max={100}
              onChange={(value: any) => {
                setIsEditing(true);
                handleDiscountPercentageChange(value);
              }}
            />
          </div>
          <div className="ad-info-content">
            <span className="ad-info-label">Discounted price</span>
            <div className="price-unit">
              <InputNumber
                className="ad-info-input"
                disabled={isStopping}
                variant="borderless"
                size="large"
                controls={false}
                value={discountedPrice}
                min={0}
                max={originalPrice}
                onChange={(value: any) => {
                  setIsEditing(true);
                  handleDiscountedPriceChange(value);
                }}
              />
              <Select
                className="ad-info-select"
                disabled={isStopping}
                value={unit}
                variant="borderless"
                style={{ width: "115px" }}
                options={[
                  { label: `${units.currency} / ton`, value: "tons" },
                  { label: `${units.currency} / bale`, value: "bale" },
                ]}
                onChange={(value: any) => {
                  setIsEditing(true);
                  handleChangeUnit(value);
                }}
              />
            </div>
          </div>
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">
              Discounted quantity{" "}
              <Tooltip
                placement="right"
                title={`Producers can set a limit on the number of products eligible for discounts. Each customer can purchase a maximum of 10 bales at the discounted price during promotion`}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <div className="price-unit">
              <InputNumber
                className="ad-info-input"
                disabled={isStopping}
                size="large"
                variant="borderless"
                controls={false}
                value={updatedAdData?.discounted_quantity}
                min={1}
                max={updatedAdData?.product.quantity}
                onChange={(value: any) => {
                  setIsEditing(true);
                  setUpdatedAdData({
                    ...updatedAdData,
                    discounted_quantity: value,
                  });
                }}
              />
              <Select
                className="ad-info-select"
                disabled={isStopping}
                value={unit}
                variant="borderless"
                style={{ width: "115px" }}
                options={[
                  { label: `ton`, value: "tons" },
                  { label: `bale`, value: "bale" },
                ]}
                onChange={(value: any) => {
                  setIsEditing(true);
                  handleChangeUnit(value);
                }}
              />
            </div>
          </div>
          <div className="ad-info-content">
            <span className="ad-info-label">
              Total discounted value{" "}
              <Tooltip
                placement="right"
                title={`Total discounted value = Discounted price * Discounted price`}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <InputNumber
              className="ad-info-input"
              disabled
              size="large"
              variant="filled"
              suffix={`${units.currency} / ${unit === "tons" ? "ton" : "bale"}`}
              controls={false}
              value={Helpers.toFixedNoRound(
                discountedPrice * updatedAdData?.discounted_quantity,
                2
              )}
            />
          </div>
        </div>
      </div>
      <div className="action-btn">
        <Button className="cancel-btn" onClick={() => handleCancel(selectedAd)}>
          Cancel
        </Button>
        <Button
          className="save-btn"
          onClick={() => {
            isReposting
              ? handleRepostAd()
              : isStopping
              ? handleChangeStatus("stop")
              : handleUpdateAd();
          }}
          disabled={!isEditing}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default AdDetail;
